import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../lib/dateUtils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { formatPhoneNumber, validatePhoneNumber } from '../utils/validation';
import { Users, Car, CheckCircle, XCircle, AlertCircle, LogOut, Plus, UserX, Baby, Trash2, User, MessageSquare, Mail, MailOpen, BarChart3, Eye, Edit, Save, HeartPulse, PhoneCall } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import NotificationBell from '../components/NotificationBell';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransportDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [trips, setTrips] = useState([]);
  const [parents, setParents] = useState([]);
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showAddParent, setShowAddParent] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showRemoveKidDialog, setShowRemoveKidDialog] = useState(false);
  const [driverToDeactivate, setDriverToDeactivate] = useState(null);
  const [kidToRemove, setKidToRemove] = useState(null);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [kidRemovalReason, setKidRemovalReason] = useState('');
  const [messages, setMessages] = useState([]);
  const [pendingLinks, setPendingLinks] = useState([]);
  const [rejectLinkKid, setRejectLinkKid] = useState(null);
  const [linkRejectionReason, setLinkRejectionReason] = useState('');
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  
  // View/Edit state
  const [viewDriver, setViewDriver] = useState(null);
  const [editDriver, setEditDriver] = useState(null);
  const [viewVehicle, setViewVehicle] = useState(null);
  const [editVehicle, setEditVehicle] = useState(null);
  const [viewKid, setViewKid] = useState(null);
  const [editKid, setEditKid] = useState(null);
  const [viewParent, setViewParent] = useState(null);
  const [editParent, setEditParent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [newDriver, setNewDriver] = useState({ full_name: '', email: '', phone: '', license_number: '', prdp_expiry_date: '' });
  const [newVehicle, setNewVehicle] = useState({ registration_number: '', make: '', model: '', year: new Date().getFullYear(), capacity: 0 });
  const [newParent, setNewParent] = useState({ full_name: '', email: '', phone: '' });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, driversRes, vehiclesRes, tripsRes, parentsRes, kidsRes, messagesRes, pendingLinksRes] = await Promise.all([
        axios.get(`${API_URL}/stats/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/drivers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/vehicles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/trips`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/parents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/transport/kids`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/hierarchy/messages/inbox`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/kids/pending-links`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setDrivers(driversRes.data.filter(d => d.status !== 'archived'));
      setVehicles(vehiclesRes.data.filter(v => v.status !== 'archived'));
      setTrips(tripsRes.data.filter(t => t.status === 'pending'));
      setParents(parentsRes.data);
      setKids(kidsRes.data);
      setMessages(messagesRes.data || []);
      setPendingLinks(pendingLinksRes.data || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddDriver = async () => {
    try {
      if (!validatePhoneNumber(newDriver.phone)) {
        toast.error('Phone number must be 11 digits (e.g., 0XX XXX XXXX)');
        return;
      }

      const response = await axios.post(`${API_URL}/drivers`, newDriver, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Driver added! Temp password: ${response.data.temp_password}`);
      setShowAddDriver(false);
      setNewDriver({ full_name: '', email: '', phone: '', license_number: '', prdp_expiry_date: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add driver');
    }
  };

  const handleAddVehicle = async () => {
    try {
      await axios.post(`${API_URL}/vehicles`, newVehicle, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vehicle added successfully');
      setShowAddVehicle(false);
      setNewVehicle({ registration_number: '', make: '', model: '', year: new Date().getFullYear(), capacity: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vehicle');
    }
  };

  const handleAssignVehicle = async (driverId, vehicleId) => {
    try {
      await axios.put(`${API_URL}/drivers/${driverId}/assign-vehicle?vehicle_id=${vehicleId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vehicle assigned successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign vehicle');
    }
  };

  const handleApproveTrip = async (tripId) => {
    try {
      await axios.put(`${API_URL}/trips/${tripId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trip approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve trip');
    }
  };

  const handleRejectTrip = async (tripId) => {
    try {
      await axios.put(`${API_URL}/trips/${tripId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trip rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject trip');
    }
  };

  const handleAddParent = async () => {
    try {
      if (!validatePhoneNumber(newParent.phone)) {
        toast.error('Phone number must be 11 digits (e.g., 0XX XXX XXXX)');
        return;
      }

      const response = await axios.post(`${API_URL}/parents`, newParent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Parent added! Temp password: ${response.data.temp_password}`);
      setShowAddParent(false);
      setNewParent({ full_name: '', email: '', phone: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add parent');
    }
  };

  const handleDeactivateDriver = async () => {
    if (!deactivationReason.trim()) {
      toast.error('Please provide a reason for deactivation');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/transport/deactivate-driver/${driverToDeactivate.driver_id}`,
        { reason: deactivationReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Driver deactivated successfully. Email notification sent to driver.');
      setShowDeactivateDialog(false);
      setDeactivationReason('');
      setDriverToDeactivate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate driver');
    }
  };

  const handleRemoveKid = async () => {
    if (!kidRemovalReason.trim()) {
      toast.error('Please provide a reason for removal');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/transport/kids/${kidToRemove.kid_id}/remove`,
        { reason: kidRemovalReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kid removed from transport service. Parent has been notified.');
      setShowRemoveKidDialog(false);
      setKidRemovalReason('');
      setKidToRemove(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove kid');
    }
  };

  const handleApproveLink = async (kid) => {
    try {
      await axios.post(`${API_URL}/kids/${kid.kid_id}/approve-link`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Link approved for ${kid.full_name}. Parent has been notified.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve link');
    }
  };

  const handleRejectLink = async () => {
    if (!rejectLinkKid) return;
    try {
      await axios.post(`${API_URL}/kids/${rejectLinkKid.kid_id}/reject-link`,
        { reason: linkRejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Link rejected for ${rejectLinkKid.full_name}. Parent has been notified.`);
      setRejectLinkKid(null);
      setLinkRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject link');
    }
  };

  const handleSendBroadcast = async () => {
    if (!broadcastTitle.trim() || !broadcastMessage.trim()) {
      toast.error('Title and message are required.');
      return;
    }
    try {
      const r = await axios.post(`${API_URL}/transport/broadcast-to-parents`,
        { title: broadcastTitle, message: broadcastMessage, notification_type: 'info' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(r.data.message || 'Broadcast sent.');
      setShowBroadcast(false);
      setBroadcastTitle('');
      setBroadcastMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send broadcast');
    }
  };


  const handleMarkMessageRead = async (messageId) => {
    try {
      await axios.put(
        `${API_URL}/hierarchy/messages/${messageId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(messages.map(m => m.message_id === messageId ? { ...m, read: true } : m));
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  };

  // Update Driver
  const handleUpdateDriver = async () => {
    try {
      await axios.put(
        `${API_URL}/drivers/${editDriver.driver_id}`,
        {
          full_name: editDriver.full_name,
          phone: editDriver.phone,
          license_number: editDriver.license_number,
          prdp_expiry_date: editDriver.prdp_expiry_date
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Driver updated successfully');
      setViewDriver(null);
      setEditDriver(null);
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update driver');
    }
  };

  // Update Vehicle
  const handleUpdateVehicle = async () => {
    try {
      await axios.put(
        `${API_URL}/vehicles/${editVehicle.vehicle_id}`,
        {
          registration_number: editVehicle.registration_number,
          make: editVehicle.make,
          model: editVehicle.model,
          year: parseInt(editVehicle.year),
          capacity: parseInt(editVehicle.capacity)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vehicle updated successfully');
      setViewVehicle(null);
      setEditVehicle(null);
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update vehicle');
    }
  };

  // Update Kid
  const handleUpdateKid = async () => {
    try {
      await axios.put(
        `${API_URL}/transport/kids/${editKid.kid_id}`,
        {
          full_name: editKid.full_name,
          grade: editKid.grade,
          pickup_address: editKid.pickup_address,
          drop_off_address: editKid.drop_off_address
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kid updated successfully');
      setViewKid(null);
      setEditKid(null);
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update kid');
    }
  };

  // Update Parent
  const handleUpdateParent = async () => {
    try {
      await axios.put(
        `${API_URL}/transport/parents/${editParent.user_id}`,
        {
          full_name: editParent.full_name,
          phone: editParent.phone
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Parent updated successfully');
      setViewParent(null);
      setEditParent(null);
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update parent');
    }
  };

  const getDriverStatusIcon = (driver) => {
    if (driver.status === 'pending') return <AlertCircle className="w-5 h-5 text-amber-500" />;
    if (driver.status === 'active' && driver.vehicle_id) return <CheckCircle className="w-5 h-5 text-emerald-500" />;
    if (driver.status === 'active' && !driver.vehicle_id) return <AlertCircle className="w-5 h-5 text-amber-500" />;
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const activeKids = kids.filter(k => k.status === 'active');
  const archivedKids = kids.filter(k => k.status === 'archived');
  const unreadMessages = messages.filter(m => !m.read).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white" data-testid="transport-dashboard">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100/50 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/ntwanayam-logo.jpg" alt="NtwanaYam Logo" className="w-12 h-12 rounded-xl object-cover shadow-sm hidden sm:block" data-testid="brand-logo" />
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground" data-testid="dashboard-title">Transport Dashboard</h1>
              <p className="text-sm text-muted-foreground" data-testid="user-name">Welcome, {user.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="default" onClick={() => navigate('/trip-analytics')} className="rounded-full" data-testid="analytics-button">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </Button>
            <NotificationBell />
            <Button variant="outline" onClick={() => setShowBroadcast(true)} className="rounded-full" data-testid="broadcast-button">
              <MessageSquare className="w-4 h-4 mr-2" />
              Broadcast
            </Button>
            <Button variant="outline" onClick={() => navigate('/profile')} className="rounded-full" data-testid="profile-button">
              <User className="w-4 h-4 mr-2" />
              Profile
            </Button>
            <Button variant="outline" onClick={logout} className="rounded-full" data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8" data-testid="stats-grid">
          <Card className="hover:shadow-lg transition-all duration-300" data-testid="stat-card-total-drivers">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Drivers</CardTitle>
              <Users className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="total-drivers-count">{stats.total_drivers || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">All active drivers</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-300" data-testid="stat-card-active-drivers">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Operational Drivers</CardTitle>
              <CheckCircle className="h-5 w-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="active-drivers-count">{stats.active_drivers || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">With assigned vehicles</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-300" data-testid="stat-card-unassigned-drivers">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Unassigned Drivers</CardTitle>
              <AlertCircle className="h-5 w-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="unassigned-drivers-count">{stats.unassigned_drivers || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Need vehicle assignment</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-300" data-testid="stat-card-pending-trips">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Pending Trips</CardTitle>
              <AlertCircle className="h-5 w-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="pending-trips-count">{stats.pending_trips || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Awaiting approval</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-300" data-testid="stat-card-kids">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Kids in Service</CardTitle>
              <Baby className="h-5 w-5 text-pink-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="kids-count">{activeKids.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Active learners</p>
            </CardContent>
          </Card>
        </div>


        {/* Pending Parent Link Approvals */}
        {pendingLinks.length > 0 && (
          <Card className="shadow-lg mb-6 border-amber-200 bg-amber-50" data-testid="pending-links-section">
            <CardHeader>
              <CardTitle className="text-2xl font-heading text-amber-800">
                Pending Parent Link Approvals
                <Badge className="ml-2 bg-amber-600">{pendingLinks.length}</Badge>
              </CardTitle>
              <CardDescription className="text-amber-700">
                Parents have requested to link to kids on your vehicles. Approve or reject within 3 working days.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingLinks.map((kid) => (
                  <div key={kid.kid_id} className="p-4 bg-white border border-amber-200 rounded-xl" data-testid={`pending-link-${kid.kid_id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <p className="font-semibold text-lg">{kid.full_name}</p>
                        <p className="text-sm text-muted-foreground">{kid.school_name} - {kid.grade}</p>
                        <div className="mt-2 text-sm">
                          <p><strong>Parent:</strong> {kid.parent_name}</p>
                          {kid.parent_email && <p className="text-muted-foreground">{kid.parent_email}</p>}
                          {kid.parent_phone && <p className="text-muted-foreground">{kid.parent_phone}</p>}
                          {kid.link_request_expires_at && (
                            <p className="text-xs text-amber-700 mt-1">
                              Expires: {formatDate(kid.link_request_expires_at)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveLink(kid)} data-testid={`approve-link-${kid.kid_id}`}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive" className="rounded-full" onClick={() => setRejectLinkKid(kid)} data-testid={`reject-link-${kid.kid_id}`}>
                          <UserX className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="drivers" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="drivers">Drivers</TabsTrigger>
            <TabsTrigger value="vehicles">Vehicles</TabsTrigger>
            <TabsTrigger value="kids">Kids ({activeKids.length})</TabsTrigger>
            <TabsTrigger value="parents">Parents</TabsTrigger>
            <TabsTrigger value="messages" className="relative">
              Messages
              {unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadMessages}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="drivers">
            <Card className="shadow-lg" data-testid="drivers-section">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-heading">Drivers</CardTitle>
                  <CardDescription>Manage your driver fleet</CardDescription>
                </div>
                <Dialog open={showAddDriver} onOpenChange={setShowAddDriver}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full" data-testid="add-driver-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Driver
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-driver-dialog">
                    <DialogHeader>
                      <DialogTitle>Add New Driver</DialogTitle>
                      <DialogDescription>Fill in the driver details below</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="full_name">Full Name</Label>
                        <Input id="full_name" value={newDriver.full_name} onChange={(e) => setNewDriver({ ...newDriver, full_name: e.target.value })} data-testid="driver-name-input" />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" type="email" value={newDriver.email} onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })} data-testid="driver-email-input" />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input id="phone" value={newDriver.phone} onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })} data-testid="driver-phone-input" />
                      </div>
                      <div>
                        <Label htmlFor="license">License Number</Label>
                        <Input id="license" value={newDriver.license_number} onChange={(e) => setNewDriver({ ...newDriver, license_number: e.target.value })} data-testid="driver-license-input" />
                      </div>
                      <div>
                        <Label htmlFor="prdp_expiry">PrDP Expiry Date</Label>
                        <Input id="prdp_expiry" type="date" value={newDriver.prdp_expiry_date} onChange={(e) => setNewDriver({ ...newDriver, prdp_expiry_date: e.target.value })} data-testid="driver-prdp-input" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleAddDriver} data-testid="submit-driver-button">Add Driver</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {drivers.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground" data-testid="no-drivers">No drivers yet</p>
                  ) : (
                    drivers.map((driver) => {
                      const availableVehicles = vehicles.filter(v => !v.assigned_driver_id || v.assigned_driver_id === driver.driver_id);
                      return (
                        <div key={driver.driver_id} className="p-4 border rounded-xl hover:shadow-md transition-all" data-testid={`driver-item-${driver.driver_id}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getDriverStatusIcon(driver)}
                              <div>
                                <h3 className="font-semibold" data-testid={`driver-name-${driver.driver_id}`}>{driver.full_name}</h3>
                                <p className="text-sm text-muted-foreground" data-testid={`driver-status-${driver.driver_id}`}>
                                  {driver.status === 'pending' && 'Pending approval'}
                                  {driver.status === 'active' && driver.vehicle_id && 'Operational'}
                                  {driver.status === 'active' && !driver.vehicle_id && 'No vehicle assigned'}
                                  {driver.status === 'rejected' && 'Rejected'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-full"
                                onClick={() => {
                                  setViewDriver(driver);
                                  setEditDriver({...driver});
                                }}
                                data-testid={`view-driver-${driver.driver_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {driver.status === 'active' && (
                                <>
                                  <Select onValueChange={(vehicleId) => handleAssignVehicle(driver.driver_id, vehicleId)}>
                                    <SelectTrigger className="w-40" data-testid={`assign-vehicle-select-${driver.driver_id}`}>
                                      <SelectValue placeholder="Assign Vehicle" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {availableVehicles.length === 0 ? (
                                        <SelectItem value="none" disabled>No vehicles available</SelectItem>
                                      ) : (
                                        availableVehicles.map(v => (
                                          <SelectItem key={v.vehicle_id} value={v.vehicle_id} data-testid={`vehicle-option-${v.vehicle_id}`}>
                                            {v.registration_number}
                                          </SelectItem>
                                        ))
                                      )}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="rounded-full"
                                    onClick={() => {
                                      setDriverToDeactivate(driver);
                                      setShowDeactivateDialog(true);
                                    }}
                                    data-testid={`deactivate-driver-${driver.driver_id}`}
                                  >
                                    <UserX className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="vehicles">
            <Card className="shadow-lg" data-testid="vehicles-section">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-heading">Vehicles</CardTitle>
                  <CardDescription>Manage your vehicle fleet</CardDescription>
                </div>
                <Dialog open={showAddVehicle} onOpenChange={setShowAddVehicle}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full" data-testid="add-vehicle-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Vehicle
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-vehicle-dialog">
                    <DialogHeader>
                      <DialogTitle>Add New Vehicle</DialogTitle>
                      <DialogDescription>Fill in the vehicle details below</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="reg_number">Registration Number</Label>
                        <Input id="reg_number" value={newVehicle.registration_number} onChange={(e) => setNewVehicle({ ...newVehicle, registration_number: e.target.value })} data-testid="vehicle-reg-input" />
                      </div>
                      <div>
                        <Label htmlFor="make">Make</Label>
                        <Input id="make" value={newVehicle.make} onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })} data-testid="vehicle-make-input" />
                      </div>
                      <div>
                        <Label htmlFor="model">Model</Label>
                        <Input id="model" value={newVehicle.model} onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })} data-testid="vehicle-model-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="year">Year</Label>
                          <Input id="year" type="number" value={newVehicle.year} onChange={(e) => setNewVehicle({ ...newVehicle, year: parseInt(e.target.value) })} data-testid="vehicle-year-input" />
                        </div>
                        <div>
                          <Label htmlFor="capacity">Capacity</Label>
                          <Input id="capacity" type="number" value={newVehicle.capacity} onChange={(e) => setNewVehicle({ ...newVehicle, capacity: parseInt(e.target.value) })} data-testid="vehicle-capacity-input" />
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleAddVehicle} data-testid="submit-vehicle-button">Add Vehicle</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {vehicles.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground" data-testid="no-vehicles">No vehicles yet</p>
                  ) : (
                    vehicles.map((vehicle) => (
                      <div key={vehicle.vehicle_id} className="p-4 border rounded-xl hover:shadow-md transition-all" data-testid={`vehicle-item-${vehicle.vehicle_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Car className="w-5 h-5 text-blue-500" />
                            <div>
                              <h3 className="font-semibold" data-testid={`vehicle-reg-${vehicle.vehicle_id}`}>{vehicle.registration_number}</h3>
                              <p className="text-sm text-muted-foreground" data-testid={`vehicle-details-${vehicle.vehicle_id}`}>
                                {vehicle.make} {vehicle.model} ({vehicle.year}) - Cap: {vehicle.capacity}
                              </p>
                              {vehicle.sticker_number && (
                                <p className="text-xs font-medium text-emerald-600 mt-1" data-testid={`vehicle-sticker-${vehicle.vehicle_id}`}>
                                  Sticker: {vehicle.sticker_number}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-full"
                                onClick={() => {
                                  setViewVehicle(vehicle);
                                  setEditVehicle({...vehicle});
                                }}
                                data-testid={`view-vehicle-${vehicle.vehicle_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Badge variant={vehicle.assigned_driver_id ? "default" : "secondary"} data-testid={`vehicle-status-${vehicle.vehicle_id}`}>
                                {vehicle.assigned_driver_id ? 'Assigned' : 'Available'}
                              </Badge>
                            </div>
                            {!vehicle.sticker_number && (
                              <span className="text-xs text-amber-600">Pending sticker</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="kids">
            <Card className="shadow-lg" data-testid="kids-section">
              <CardHeader>
                <CardTitle className="text-2xl font-heading">Kids in Service</CardTitle>
                <CardDescription>View and manage kids registered for your transport service</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {kids.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground" data-testid="no-kids">No kids registered yet</p>
                  ) : (
                    <>
                      {/* Active kids */}
                      {activeKids.map((kid) => (
                        <div key={kid.kid_id} className={`p-4 border rounded-xl hover:shadow-md transition-all ${(kid.medical_alerts?.length > 0 || kid.medical_notes) ? 'border-red-300' : ''}`} data-testid={`kid-item-${kid.kid_id}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Baby className="w-5 h-5 text-pink-500" />
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold" data-testid={`kid-name-${kid.kid_id}`}>{kid.full_name}</h3>
                                  {(kid.medical_alerts?.length > 0 || kid.medical_notes) && (
                                    <Badge className="bg-red-600 text-white gap-1" data-testid={`kid-medical-badge-${kid.kid_id}`}>
                                      <HeartPulse className="w-3 h-3" /> Medical
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">{kid.school_name} - {kid.grade}</p>
                                <p className="text-xs text-muted-foreground">Parent: {kid.parent_name}</p>
                                <p className="text-xs text-muted-foreground">Pickup: {kid.pickup_address}</p>
                                {(kid.medical_alerts?.length > 0 || kid.medical_notes) && (
                                  <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2" data-testid={`medical-inline-${kid.kid_id}`}>
                                    {kid.medical_alerts?.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mb-1">
                                        {kid.medical_alerts.map((a) => (
                                          <span key={a} className="text-xs bg-red-100 text-red-800 border border-red-200 px-1.5 py-0.5 rounded">{a}</span>
                                        ))}
                                      </div>
                                    )}
                                    {kid.medical_notes && (
                                      <p className="text-xs text-red-900 whitespace-pre-wrap">{kid.medical_notes}</p>
                                    )}
                                  </div>
                                )}
                                {(kid.emergency_contacts?.length > 0) && (
                                  <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-2" data-testid={`emergency-inline-${kid.kid_id}`}>
                                    <div className="flex items-center gap-1 text-blue-800 font-semibold text-xs mb-1">
                                      <PhoneCall className="w-3.5 h-3.5" /> Emergency contacts
                                    </div>
                                    <ul className="space-y-0.5">
                                      {kid.emergency_contacts.map((c, i) => (
                                        <li key={i} className="text-xs text-blue-900">
                                          {c.name || 'Unnamed'}{c.relationship ? ` (${c.relationship})` : ''}
                                          {c.phone && (
                                            <> — <a href={`tel:${c.phone}`} className="underline font-medium">{c.phone}</a></>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-full"
                                onClick={() => {
                                  setViewKid(kid);
                                  setEditKid({...kid});
                                }}
                                data-testid={`view-kid-${kid.kid_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Badge variant="default">Active</Badge>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="rounded-full"
                                onClick={() => {
                                  setKidToRemove(kid);
                                  setShowRemoveKidDialog(true);
                                }}
                                data-testid={`remove-kid-${kid.kid_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {/* Archived kids */}
                      {archivedKids.length > 0 && (
                        <>
                          <div className="border-t my-4 pt-4">
                            <p className="text-sm text-muted-foreground font-medium mb-3">Removed Kids ({archivedKids.length})</p>
                          </div>
                          {archivedKids.map((kid) => (
                            <div key={kid.kid_id} className="p-4 border rounded-xl bg-slate-50 opacity-60" data-testid={`kid-item-${kid.kid_id}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Baby className="w-5 h-5 text-gray-400" />
                                  <div>
                                    <h3 className="font-semibold text-gray-600">{kid.full_name}</h3>
                                    <p className="text-sm text-muted-foreground">{kid.school_name} - {kid.grade}</p>
                                    {kid.removal_reason && (
                                      <p className="text-xs text-red-500 mt-1">Removal reason: {kid.removal_reason}</p>
                                    )}
                                  </div>
                                </div>
                                <Badge variant="secondary">Archived</Badge>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="parents">
            <Card className="shadow-lg" data-testid="parents-section">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-heading">Parents</CardTitle>
                  <CardDescription>Manage parent accounts</CardDescription>
                </div>
                <Dialog open={showAddParent} onOpenChange={setShowAddParent}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full" data-testid="add-parent-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Parent
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-parent-dialog">
                    <DialogHeader>
                      <DialogTitle>Add New Parent</DialogTitle>
                      <DialogDescription>Add a parent account who can register their children</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="parent_name">Full Name</Label>
                        <Input id="parent_name" value={newParent.full_name} onChange={(e) => setNewParent({ ...newParent, full_name: e.target.value })} data-testid="parent-name-input" />
                      </div>
                      <div>
                        <Label htmlFor="parent_email">Email</Label>
                        <Input id="parent_email" type="email" value={newParent.email} onChange={(e) => setNewParent({ ...newParent, email: e.target.value })} data-testid="parent-email-input" />
                      </div>
                      <div>
                        <Label htmlFor="parent_phone">Phone</Label>
                        <Input id="parent_phone" value={newParent.phone} onChange={(e) => setNewParent({ ...newParent, phone: e.target.value })} data-testid="parent-phone-input" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleAddParent} data-testid="submit-parent-button">Add Parent</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {parents.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground" data-testid="no-parents">No parents yet</p>
                  ) : (
                    parents.map((parent) => (
                      <div key={parent.user_id} className="p-4 border rounded-xl hover:shadow-md transition-all" data-testid={`parent-item-${parent.user_id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold" data-testid={`parent-name-${parent.user_id}`}>{parent.full_name}</h3>
                            <p className="text-sm text-muted-foreground" data-testid={`parent-email-${parent.user_id}`}>{parent.email}</p>
                            <p className="text-xs text-muted-foreground mt-1" data-testid={`parent-phone-${parent.user_id}`}>{parent.phone}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full"
                              onClick={() => {
                                setViewParent(parent);
                                setEditParent({...parent});
                              }}
                              data-testid={`view-parent-${parent.user_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Badge variant="default">Active</Badge>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages">
            <Card className="shadow-lg" data-testid="messages-section">
              <CardHeader>
                <div>
                  <CardTitle className="text-2xl font-heading">Messages from Admin</CardTitle>
                  <CardDescription>Broadcast messages from administrators</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {messages.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground" data-testid="no-messages">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No messages yet</p>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div 
                        key={message.message_id} 
                        className={`p-4 border rounded-xl hover:shadow-md transition-all ${!message.read ? 'bg-blue-50 border-blue-200' : ''}`} 
                        data-testid={`message-item-${message.message_id}`}
                        onClick={() => !message.read && handleMarkMessageRead(message.message_id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            {message.read ? (
                              <MailOpen className="w-5 h-5 text-gray-400 mt-1" />
                            ) : (
                              <Mail className="w-5 h-5 text-blue-500 mt-1" />
                            )}
                            <div>
                              <h3 className="font-semibold" data-testid={`message-subject-${message.message_id}`}>
                                {message.subject || 'Broadcast Message'}
                              </h3>
                              <p className="text-sm text-muted-foreground mt-1" data-testid={`message-content-${message.message_id}`}>
                                {message.message}
                              </p>
                              <p className="text-xs text-muted-foreground mt-2">
                                From: {message.sender_name || 'Administrator'} • {formatDate(message.created_at)}
                              </p>
                            </div>
                          </div>
                          {!message.read && (
                            <Badge variant="default" className="bg-blue-500">New</Badge>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Card className="shadow-lg mt-8" data-testid="pending-trips-section">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Pending Trip Approvals</CardTitle>
            <CardDescription>Review and approve or reject trip requests</CardDescription>
          </CardHeader>
          <CardContent>
            {trips.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-pending-trips">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                <p>No pending trip requests</p>
              </div>
            ) : (
              <div className="space-y-4">
                {trips.map((trip) => {
                  const driver = drivers.find(d => d.driver_id === trip.driver_id);
                  return (
                    <div key={trip.trip_id} className="flex items-center justify-between p-4 border rounded-xl hover:shadow-md transition-all" data-testid={`trip-item-${trip.trip_id}`}>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg" data-testid={`trip-route-${trip.trip_id}`}>{trip.route_name}</h3>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span data-testid={`trip-driver-${trip.trip_id}`}>Driver: {driver?.full_name}</span>
                          <span data-testid={`trip-date-${trip.trip_id}`}>{trip.start_date} to {trip.end_date} at {trip.scheduled_time}</span>
                          <span data-testid={`trip-learners-${trip.trip_id}`}>{trip.learner_ids?.length || trip.kid_ids?.length || 0} learners</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="rounded-full" onClick={() => handleApproveTrip(trip.trip_id)} data-testid={`approve-trip-${trip.trip_id}`}>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button size="sm" variant="destructive" className="rounded-full" onClick={() => handleRejectTrip(trip.trip_id)} data-testid={`reject-trip-${trip.trip_id}`}>
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Deactivate Driver Confirmation Dialog */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent data-testid="deactivate-driver-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-red-600">Deactivate Driver</DialogTitle>
            <DialogDescription>
              You are about to deactivate <strong>{driverToDeactivate?.full_name}</strong>. 
              This action will prevent the driver from accessing the system.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
              <AlertCircle className="w-4 h-4 inline-block mr-2" />
              The driver will receive an email notification about this deactivation. 
              The driver will be removed from your active drivers list but will remain visible to administrators.
            </div>
            <div className="space-y-2">
              <Label>Reason for Deactivation *</Label>
              <Textarea
                placeholder="Provide a detailed reason for deactivating this driver..."
                value={deactivationReason}
                onChange={(e) => setDeactivationReason(e.target.value)}
                rows={3}
                data-testid="deactivation-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeactivateDialog(false);
                setDeactivationReason('');
                setDriverToDeactivate(null);
              }}
              data-testid="cancel-deactivation"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeactivateDriver}
              data-testid="confirm-deactivation"
            >
              <UserX className="w-4 h-4 mr-2" />
              Confirm Deactivation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Kid Confirmation Dialog */}
      <Dialog open={showRemoveKidDialog} onOpenChange={setShowRemoveKidDialog}>
        <DialogContent data-testid="remove-kid-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-red-600">Remove Kid from Service</DialogTitle>
            <DialogDescription>
              You are about to remove <strong>{kidToRemove?.full_name}</strong> from your transport service. 
              The parent will be notified of this action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
              <AlertCircle className="w-4 h-4 inline-block mr-2" />
              The parent ({kidToRemove?.parent_name}) will receive an email notification about this removal.
            </div>
            <div className="space-y-2">
              <Label>Reason for Removal *</Label>
              <Textarea
                placeholder="Provide a detailed reason for removing this kid from your service..."
                value={kidRemovalReason}
                onChange={(e) => setKidRemovalReason(e.target.value)}
                rows={3}
                data-testid="kid-removal-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRemoveKidDialog(false);
                setKidRemovalReason('');
                setKidToRemove(null);
              }}
              data-testid="cancel-kid-removal"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveKid}
              data-testid="confirm-kid-removal"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Confirm Removal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast to Parents Dialog */}
      <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
        <DialogContent data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle>Broadcast to All Linked Parents</DialogTitle>
            <DialogDescription>
              Send a notification to every parent whose child is linked to any of your vehicles. They will see it in their app immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input value={broadcastTitle} onChange={(e) => setBroadcastTitle(e.target.value)} maxLength={120} placeholder="e.g., Route delayed 15 minutes today" data-testid="broadcast-title-input" />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea value={broadcastMessage} onChange={(e) => setBroadcastMessage(e.target.value)} maxLength={2000} rows={4} placeholder="e.g., Due to a road accident on the R21, our vehicles are running about 15 minutes behind schedule. Please expect delayed pickups. Thanks for your patience." data-testid="broadcast-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBroadcast(false)}>Cancel</Button>
            <Button onClick={handleSendBroadcast} data-testid="send-broadcast-btn">
              <MessageSquare className="w-4 h-4 mr-2" /> Send Broadcast
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Parent Link Dialog */}
      <Dialog open={!!rejectLinkKid} onOpenChange={(open) => { if (!open) { setRejectLinkKid(null); setLinkRejectionReason(''); } }}>
        <DialogContent data-testid="reject-link-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Reject Parent Link</DialogTitle>
            <DialogDescription>
              Reject the link request from <strong>{rejectLinkKid?.parent_name}</strong> to <strong>{rejectLinkKid?.full_name}</strong>. The parent will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                value={linkRejectionReason}
                onChange={(e) => setLinkRejectionReason(e.target.value)}
                placeholder="e.g., We do not recognise this parent."
                rows={3}
                data-testid="reject-link-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectLinkKid(null); setLinkRejectionReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectLink} data-testid="confirm-reject-link-btn">
              <UserX className="w-4 h-4 mr-2" /> Reject Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* View/Edit Driver Dialog */}
      <Dialog open={!!viewDriver} onOpenChange={(open) => { if (!open) { setViewDriver(null); setEditDriver(null); setIsEditing(false); }}}>
        <DialogContent className="max-w-lg" data-testid="view-driver-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEditing ? 'Edit Driver' : 'Driver Details'}</span>
              {!isEditing && (
                <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-driver-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewDriver && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  {isEditing ? (
                    <Input value={editDriver?.full_name || ''} onChange={(e) => setEditDriver({...editDriver, full_name: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewDriver.full_name}</p>
                  )}
                </div>
                <div>
                  <Label>Email</Label>
                  <p className="text-sm font-medium mt-1">{viewDriver.email}</p>
                </div>
                <div>
                  <Label>Phone</Label>
                  {isEditing ? (
                    <Input value={editDriver?.phone || ''} onChange={(e) => setEditDriver({...editDriver, phone: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewDriver.phone || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <Label>License Number</Label>
                  {isEditing ? (
                    <Input value={editDriver?.license_number || ''} onChange={(e) => setEditDriver({...editDriver, license_number: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewDriver.license_number || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <Label>PrDP Expiry</Label>
                  {isEditing ? (
                    <Input type="date" value={editDriver?.prdp_expiry_date || ''} onChange={(e) => setEditDriver({...editDriver, prdp_expiry_date: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewDriver.prdp_expiry_date || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <Label>Status</Label>
                  <Badge className="mt-1" variant={viewDriver.status === 'active' ? 'default' : 'secondary'}>{viewDriver.status}</Badge>
                </div>
              </div>
              {isEditing && (
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setIsEditing(false); setEditDriver({...viewDriver}); }}>Cancel</Button>
                  <Button onClick={handleUpdateDriver} data-testid="save-driver-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View/Edit Vehicle Dialog */}
      <Dialog open={!!viewVehicle} onOpenChange={(open) => { if (!open) { setViewVehicle(null); setEditVehicle(null); setIsEditing(false); }}}>
        <DialogContent className="max-w-lg" data-testid="view-vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEditing ? 'Edit Vehicle' : 'Vehicle Details'}</span>
              {!isEditing && (
                <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-vehicle-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewVehicle && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Registration Number</Label>
                  {isEditing ? (
                    <Input value={editVehicle?.registration_number || ''} onChange={(e) => setEditVehicle({...editVehicle, registration_number: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewVehicle.registration_number}</p>
                  )}
                </div>
                <div>
                  <Label>Make</Label>
                  {isEditing ? (
                    <Input value={editVehicle?.make || ''} onChange={(e) => setEditVehicle({...editVehicle, make: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewVehicle.make}</p>
                  )}
                </div>
                <div>
                  <Label>Model</Label>
                  {isEditing ? (
                    <Input value={editVehicle?.model || ''} onChange={(e) => setEditVehicle({...editVehicle, model: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewVehicle.model}</p>
                  )}
                </div>
                <div>
                  <Label>Year</Label>
                  {isEditing ? (
                    <Input type="number" value={editVehicle?.year || ''} onChange={(e) => setEditVehicle({...editVehicle, year: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewVehicle.year}</p>
                  )}
                </div>
                <div>
                  <Label>Capacity</Label>
                  {isEditing ? (
                    <Input type="number" value={editVehicle?.capacity || ''} onChange={(e) => setEditVehicle({...editVehicle, capacity: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewVehicle.capacity} seats</p>
                  )}
                </div>
                <div>
                  <Label>Status</Label>
                  <Badge className="mt-1" variant={viewVehicle.status === 'active' ? 'default' : 'secondary'}>{viewVehicle.status}</Badge>
                </div>
                {viewVehicle.sticker_number && (
                  <div>
                    <Label>Sticker Number</Label>
                    <p className="text-sm font-medium mt-1 text-emerald-600">{viewVehicle.sticker_number}</p>
                  </div>
                )}
              </div>
              {isEditing && (
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setIsEditing(false); setEditVehicle({...viewVehicle}); }}>Cancel</Button>
                  <Button onClick={handleUpdateVehicle} data-testid="save-vehicle-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View/Edit Kid Dialog */}
      <Dialog open={!!viewKid} onOpenChange={(open) => { if (!open) { setViewKid(null); setEditKid(null); setIsEditing(false); }}}>
        <DialogContent className="max-w-lg" data-testid="view-kid-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEditing ? 'Edit Kid' : 'Kid Details'}</span>
              {!isEditing && (
                <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-kid-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewKid && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  {isEditing ? (
                    <Input value={editKid?.full_name || ''} onChange={(e) => setEditKid({...editKid, full_name: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewKid.full_name}</p>
                  )}
                </div>
                <div>
                  <Label>Grade</Label>
                  {isEditing ? (
                    <Input value={editKid?.grade || ''} onChange={(e) => setEditKid({...editKid, grade: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewKid.grade}</p>
                  )}
                </div>
                <div>
                  <Label>School</Label>
                  <p className="text-sm font-medium mt-1">{viewKid.school_name}</p>
                </div>
                <div>
                  <Label>Parent</Label>
                  <p className="text-sm font-medium mt-1">{viewKid.parent_name || 'Not linked'}</p>
                </div>
                <div className="col-span-2">
                  <Label>Pickup Address (Morning)</Label>
                  {isEditing ? (
                    <Input value={editKid?.pickup_address || ''} onChange={(e) => setEditKid({...editKid, pickup_address: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewKid.pickup_address}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <Label>Drop-off Address (Afternoon)</Label>
                  {isEditing ? (
                    <Input value={editKid?.drop_off_address || ''} onChange={(e) => setEditKid({...editKid, drop_off_address: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewKid.drop_off_address || viewKid.pickup_address}</p>
                  )}
                </div>
                <div>
                  <Label>Kid ID</Label>
                  <p className="text-xs font-mono mt-1 bg-slate-100 p-1 rounded break-all">{viewKid.kid_id}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <Badge className="mt-1" variant={viewKid.status === 'active' ? 'default' : 'secondary'}>{viewKid.status}</Badge>
                </div>
              </div>
              {isEditing && (
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setIsEditing(false); setEditKid({...viewKid}); }}>Cancel</Button>
                  <Button onClick={handleUpdateKid} data-testid="save-kid-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View/Edit Parent Dialog */}
      <Dialog open={!!viewParent} onOpenChange={(open) => { if (!open) { setViewParent(null); setEditParent(null); setIsEditing(false); }}}>
        <DialogContent className="max-w-lg" data-testid="view-parent-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEditing ? 'Edit Parent' : 'Parent Details'}</span>
              {!isEditing && (
                <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-parent-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewParent && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  {isEditing ? (
                    <Input value={editParent?.full_name || ''} onChange={(e) => setEditParent({...editParent, full_name: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewParent.full_name}</p>
                  )}
                </div>
                <div>
                  <Label>Email</Label>
                  <p className="text-sm font-medium mt-1">{viewParent.email}</p>
                </div>
                <div>
                  <Label>Phone</Label>
                  {isEditing ? (
                    <Input value={editParent?.phone || ''} onChange={(e) => setEditParent({...editParent, phone: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewParent.phone || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <Label>Status</Label>
                  <Badge className="mt-1" variant={viewParent.status === 'active' ? 'default' : 'secondary'}>{viewParent.status}</Badge>
                </div>
              </div>
              {isEditing && (
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setIsEditing(false); setEditParent({...viewParent}); }}>Cancel</Button>
                  <Button onClick={handleUpdateParent} data-testid="save-parent-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
